import { useState } from 'react';

export default function HomePage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus('submitting');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5p161g907tfb5jlt9l0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setSubmitStatus('idle'), 3000);
      } else {
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus('idle'), 3000);
      }
    } catch (error) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 3000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > 500) return;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Company Name - Top Left */}
        <div className="absolute top-8 left-[8%] z-20">
          <div className="flex items-center gap-3">
            <img 
              src="https://public.readdy.ai/ai/img_res/2dc93455-661a-4844-bf85-76e3bcbbc1cc.png" 
              alt="CASELLA S.R.L. Logo" 
              className="h-10 w-auto object-contain"
            />
            <h1 className="text-sm tracking-[0.2em] font-extralight text-[#1a1a1a]">CASELLA S.R.L.</h1>
          </div>
        </div>

        {/* Left Content Area - 70% */}
        <div className="w-[70%] pl-[8%] pt-[15%]">
          <div className="max-w-3xl">
            <h2 className="text-[72px] font-extrabold text-[#1a1a1a] leading-[1.1] mb-16">
              <div>Modern iOS Applications</div>
              <div className="ml-5">Built for Security</div>
              <div className="ml-10">& Performance</div>
            </h2>
            <p className="text-lg text-[#6b6b6b] max-w-[480px] leading-relaxed">
              We are a team of experts developing cutting-edge applications for the iOS platform. Our products, including VPN and Cleaner solutions, are designed to enhance user experience and improve the security of mobile devices.
            </p>
          </div>
        </div>

        {/* Right Visual Area - 30% */}
        <div className="absolute right-0 top-0 w-[30%] h-full bg-gradient-to-br from-blue-50 via-blue-100/30 to-[#faf8f5]"></div>

        {/* Floating Device Mockup */}
        <div className="absolute bottom-[-5%] right-[10%] w-80 h-96 bg-gradient-to-br from-blue-400 to-blue-600 rounded-[32px] shadow-2xl transform rotate-6 opacity-90"></div>
      </section>

      {/* About Section */}
      <section className="relative py-32 overflow-hidden">
        {/* Left Decorative */}
        <div className="absolute left-0 top-0 w-[40%] h-full bg-gradient-to-br from-pink-50 to-pink-100/50">
          <div className="absolute left-[-10%] top-1/2 -translate-y-1/2 text-[280px] font-bold text-transparent" style={{ WebkitTextStroke: '2px #ffc0cb' }}>
            01
          </div>
        </div>

        {/* Right Content Area */}
        <div className="relative ml-[40%] pr-[8%] pl-16">
          <h3 className="text-5xl font-medium text-[#1a1a1a] mb-8">Expert Team</h3>
          <p className="text-xl text-[#4a4a4a] leading-[1.8] max-w-[520px]">
            Our dedicated team combines technical excellence with creative innovation to deliver iOS applications that set new standards in mobile security and performance optimization. We focus on creating intuitive solutions that protect and enhance your digital life.
          </p>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-32 px-[8%]">
        <div className="max-w-[1280px] mx-auto">
          {/* Header */}
          <div className="flex justify-between items-start mb-20">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-gray-400 mb-4">OUR PRODUCTS</div>
              <h3 className="text-[56px] leading-tight">
                <div className="font-light italic">Innovative Solutions</div>
                <div className="font-bold">for iOS Users</div>
              </h3>
            </div>
            <p className="text-lg text-[#6b6b6b] max-w-[420px] leading-relaxed mt-8">
              Each product is crafted with precision and care, focusing on delivering exceptional value and seamless user experiences across all iOS devices.
            </p>
          </div>

          {/* Product Grid */}
          <div className="grid grid-cols-3 gap-8">
            {/* VPN Product */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden cursor-pointer hover:shadow-xl transition-shadow">
              <div className="h-80 relative overflow-hidden">
                <img 
                  src="https://public.readdy.ai/ai/img_res/3a60a429237ebdb1f054f7d784bec122.jpg" 
                  alt="VPN Solution"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              </div>
              <div className="p-8">
                <div className="inline-block bg-[#d4ff00] px-4 py-1.5 rounded-full mb-4">
                  <span className="text-xs font-bold text-[#1a1a1a]">VPN SOLUTION</span>
                </div>
                <h4 className="text-[28px] font-bold text-[#1a1a1a] leading-tight">
                  Secure VPN Protection for Your Privacy
                </h4>
              </div>
            </div>

            {/* Security Feature */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden cursor-pointer hover:shadow-xl transition-shadow">
              <div className="h-80 relative overflow-hidden bg-[#1a1a1a] flex items-center justify-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20pattern%20with%20neon%20yellow-green%20accents%20on%20dark%20charcoal%20background%2C%20modern%20tech%20security%20visualization%20with%20hexagonal%20shapes%20and%20circuit%20board%20elements%2C%20minimalist%20digital%20security%20concept%20art&width=400&height=500&seq=sec001&orientation=portrait" 
                  alt="Security"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8">
                <div className="inline-block bg-[#d4ff00] px-4 py-1.5 rounded-full mb-4">
                  <span className="text-xs font-bold text-[#1a1a1a]">SECURITY</span>
                </div>
                <h4 className="text-[28px] font-bold text-[#1a1a1a] leading-tight">
                  Advanced Encryption Technology
                </h4>
              </div>
            </div>

            {/* Cleaner Product */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden cursor-pointer hover:shadow-xl transition-shadow">
              <div className="h-80 relative overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=iPhone%20cleaner%20app%20interface%20showing%20storage%20optimization%20with%20colorful%20charts%20and%20file%20management%2C%20modern%20iOS%20application%20design%20with%20clean%20white%20background%2C%20professional%20mobile%20utility%20app%20screenshot%20displaying%20memory%20usage%20statistics&width=400&height=500&seq=clean001&orientation=portrait" 
                  alt="Cleaner Optimization"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
              <div className="p-8">
                <div className="inline-block bg-[#d4ff00] px-4 py-1.5 rounded-full mb-4">
                  <span className="text-xs font-bold text-[#1a1a1a]">OPTIMIZATION</span>
                </div>
                <h4 className="text-[28px] font-bold text-[#1a1a1a] leading-tight">
                  Smart Device Cleaner & Optimizer
                </h4>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Highlight Section */}
      <section className="py-32 px-[8%]">
        <div className="max-w-[1280px] mx-auto flex items-center gap-20">
          {/* Left Content - 45% */}
          <div className="w-[45%]">
            <div className="text-[11px] uppercase tracking-[0.4em] text-gray-400 mb-5">CORE FEATURES</div>
            <h3 className="text-[58px] font-bold text-[#1a1a1a] leading-tight mb-10">
              <div>Security First</div>
              <div>Performance Driven</div>
              <div>User Focused</div>
            </h3>
            <p className="text-[19px] text-[#6b6b6b] leading-[1.8] max-w-[440px]">
              Our technical approach combines industry-leading security protocols with optimized performance algorithms. Every line of code is written with the user in mind, ensuring that our applications not only protect but also enhance the iOS experience through intelligent design and seamless functionality.
            </p>
          </div>

          {/* Right Visual - 55% */}
          <div className="w-[55%] relative">
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-[40px] p-16 relative overflow-visible min-h-[500px] flex items-center justify-center">
              <img 
                src="https://readdy.ai/api/search-image?query=Floating%20iOS%20app%20interface%20elements%20with%20security%20badges%20and%20performance%20icons%2C%20modern%20tech%20illustration%20with%20scattered%20UI%20fragments%20showing%20VPN%20shield%20and%20cleaner%20optimization%20symbols%20on%20light%20blue%20gradient%20background%2C%20professional%20software%20development%20concept&width=600&height=500&seq=feat001&orientation=landscape" 
                alt="Core Features"
                className="w-full h-auto object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-36 px-[8%] bg-white">
        <div className="max-w-[900px] mx-auto">
          <div className="rounded-[28px] overflow-hidden mb-14 mx-2">
            <img 
              src="https://readdy.ai/api/search-image?query=Modern%20workspace%20with%20iPhone%20and%20iPad%20devices%20on%20minimalist%20desk%2C%20professional%20iOS%20development%20environment%20with%20clean%20aesthetic%2C%20natural%20lighting%20and%20premium%20tech%20setup%20showing%20mobile%20applications%2C%20lifestyle%20photography%20with%20film%20grain%20texture&width=900&height=500&seq=cta001&orientation=landscape" 
              alt="iOS Development"
              className="w-full h-auto object-cover"
            />
          </div>
          <div className="text-center">
            <h3 className="text-[42px] leading-[1.3] tracking-tight">
              <div className="font-light italic text-[#7a7a7a]">Ready to enhance</div>
              <div className="font-bold text-[#1a1a1a]">your mobile experience</div>
              <div className="font-light italic text-[#7a7a7a]">with our solutions</div>
            </h3>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 px-[8%] bg-[#1a3a2e] relative overflow-hidden">
        <div className="max-w-[1280px] mx-auto relative z-10">
          {/* Upper Grid */}
          <div className="grid grid-cols-4 gap-12 mb-24">
            {/* Company Info */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img 
                  src="https://public.readdy.ai/ai/img_res/2dc93455-661a-4844-bf85-76e3bcbbc1cc.png" 
                  alt="CASELLA S.R.L. Logo" 
                  className="h-8 w-auto object-contain brightness-0 invert"
                />
                <div className="text-lg font-semibold text-[#faf8f5]">CASELLA S.R.L.</div>
              </div>
              <p className="text-sm text-[#a0a0a0] leading-[1.6] max-w-[240px]">
                Expert team developing modern iOS applications focused on security and performance for mobile devices worldwide.
              </p>
            </div>

            {/* Newsletter */}
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#faf8f5] mb-5">STAY UPDATED</div>
              <form className="relative">
                <input 
                  type="email" 
                  placeholder="Enter your email"
                  className="w-full bg-transparent border-b border-[#faf8f5] pb-3 text-[15px] text-[#faf8f5] placeholder-gray-400 focus:outline-none"
                />
                <button type="submit" className="absolute right-0 bottom-3 text-[#faf8f5] text-xl cursor-pointer hover:opacity-70 transition-opacity">
                  <i className="ri-arrow-right-line"></i>
                </button>
              </form>
              <div className="text-xs text-gray-400 mt-3 underline cursor-pointer hover:text-gray-300 transition-colors">Privacy policy</div>
            </div>

            {/* Quick Links */}
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#faf8f5] mb-6">EXPLORE</div>
              <div className="space-y-5">
                <div className="text-[15px] text-[#faf8f5] cursor-pointer hover:text-[#d4ff00] transition-colors whitespace-nowrap">Products</div>
                <div className="text-[15px] text-[#faf8f5] cursor-pointer hover:text-[#d4ff00] transition-colors whitespace-nowrap">Technology</div>
                <div className="text-[15px] text-[#faf8f5] cursor-pointer hover:text-[#d4ff00] transition-colors whitespace-nowrap">Company</div>
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#faf8f5] mb-6">CONNECT</div>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-4 h-4 flex items-center justify-center mt-0.5">
                    <i className="ri-map-pin-line text-[#faf8f5] text-sm"></i>
                  </div>
                  <div className="text-sm leading-relaxed">
                    <div className="text-[#a0a0a0]">Address:</div>
                    <div className="text-[#faf8f5]">MITRE 637</div>
                    <div className="text-[#faf8f5]">Viedma, R8500DUM</div>
                    <div className="text-[#faf8f5]">Argentina</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-mail-line text-[#faf8f5] text-sm"></i>
                  </div>
                  <div className="text-sm">
                    <div className="text-[#a0a0a0]">Email:</div>
                    <a href="mailto:help@casella-srl.com" className="text-[#faf8f5] hover:text-[#d4ff00] transition-colors">help@casella-srl.com</a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-phone-line text-[#faf8f5] text-sm"></i>
                  </div>
                  <div className="text-sm">
                    <div className="text-[#a0a0a0]">Phone:</div>
                    <a href="tel:+18046092374" className="text-[#faf8f5] hover:text-[#d4ff00] transition-colors">+1 8046092374</a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="max-w-2xl mx-auto mb-24">
            <div className="text-center mb-12">
              <h3 className="text-4xl font-bold text-[#faf8f5] mb-4">Get in Touch</h3>
              <p className="text-lg text-[#a0a0a0]">Have questions? We'd love to hear from you.</p>
            </div>
            
            <form onSubmit={handleSubmit} data-readdy-form id="contact_form" className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm text-[#faf8f5] mb-2">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/10 border border-[#faf8f5]/30 rounded-lg px-4 py-3 text-[#faf8f5] placeholder-gray-400 focus:outline-none focus:border-[#d4ff00] transition-colors"
                  placeholder="Your name"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm text-[#faf8f5] mb-2">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/10 border border-[#faf8f5]/30 rounded-lg px-4 py-3 text-[#faf8f5] placeholder-gray-400 focus:outline-none focus:border-[#d4ff00] transition-colors"
                  placeholder="your@email.com"
                />
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm text-[#faf8f5] mb-2">
                  Message ({formData.message.length}/500)
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full bg-white/10 border border-[#faf8f5]/30 rounded-lg px-4 py-3 text-[#faf8f5] placeholder-gray-400 focus:outline-none focus:border-[#d4ff00] transition-colors resize-none"
                  placeholder="Your message..."
                />
              </div>
              
              <button
                type="submit"
                disabled={submitStatus === 'submitting'}
                className="w-full bg-[#d4ff00] text-[#1a1a1a] font-bold py-4 rounded-lg hover:bg-[#c4ef00] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {submitStatus === 'submitting' ? 'Sending...' : 'Send Message'}
              </button>
              
              {submitStatus === 'success' && (
                <div className="text-center text-[#d4ff00] text-sm">Message sent successfully!</div>
              )}
              {submitStatus === 'error' && (
                <div className="text-center text-red-400 text-sm">Failed to send message. Please try again.</div>
              )}
            </form>
          </div>

          {/* Lower Branding Area */}
          <div className="relative">
            <div className="text-[180px] font-bold text-transparent leading-none tracking-tight" style={{ WebkitTextStroke: '2px #faf8f5' }}>
              CASELLA
            </div>
            <div className="absolute bottom-10 left-0 text-xs text-[#a0a0a0]">
              © 2025 CASELLA S.R.L. All rights reserved. | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#d4ff00] transition-colors cursor-pointer">Website Builder</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
